import { ProductTileModel } from '@notino/react-styleguide';
import { IStock } from '@notino/shared/definitions/custom-definitions';
import { GetProductsByIdBatchedQuery } from '@notino/shared/definitions/types';
import {
  DiscountNameType,
  DiscountType,
  IProductProps,
  IProductTag,
  IPromotionData,
  ProductCardModel,
  ProductTagType,
  StockAvailability,
} from '@notino/web-libraries-product-card';

import { transformAttributesToTags } from '@components/Tags/utils';
import { IConditionalFreeDelivery } from '@containers/ProductDetailContainer/ProductDetail/ProductBaseInfo/ConditionalFreeDelivery/model';
import { resolveGlamourDay } from '@containers/ProductDetailContainer/ProductDetail/ProductBaseInfo/VoucherDiscount/GlamourDay/utils';
import { resolveVoucherDiscount } from '@containers/ProductDetailContainer/ProductDetail/ProductBaseInfo/VoucherDiscount/useResolvedVoucherDiscount';
import { isConditionalDiscount } from '@containers/ProductDetailContainer/ProductDetail/ProductBaseInfo/VoucherDiscount/utils';
import {
  AttributeType,
  AttributeTypes,
} from '@containers/ProductListing/model';
import { noop } from '@utils/noop';

import { transformProductIcons } from './utils';

export type VpProduct = NonNullable<
  GetProductsByIdBatchedQuery['vpProductByIds'][number]
>;

const LABEL_TAGS: Partial<
  Record<
    ProductTileModel.ProductLabelAttributes,
    ProductCardModel.ProductLabelAttributes
  >
> = {
  [ProductTileModel.ProductLabelAttributes.Action]:
    ProductCardModel.ProductLabelAttributes.OnSale,
  [ProductTileModel.ProductLabelAttributes.FreeDelivery]:
    ProductCardModel.ProductLabelAttributes.FreeDelivery,
  [ProductTileModel.ProductLabelAttributes.New]:
    ProductCardModel.ProductLabelAttributes.NewArrival,
  [ProductTileModel.ProductLabelAttributes.Clearance]:
    ProductCardModel.ProductLabelAttributes.Clearance,
  [ProductTileModel.ProductLabelAttributes.Damaged]:
    ProductCardModel.ProductLabelAttributes.Damaged,
  [ProductTileModel.ProductLabelAttributes.Returned]:
    ProductCardModel.ProductLabelAttributes.Returned,
  [ProductTileModel.ProductLabelAttributes.Gift]:
    ProductCardModel.ProductLabelAttributes.FreeGift,
  [ProductTileModel.ProductLabelAttributes.VirtualTryOn]:
    ProductCardModel.ProductLabelAttributes.VirtualTryOn,
  [ProductTileModel.ProductLabelAttributes.BargainSet]:
    ProductCardModel.ProductLabelAttributes.BargainSet,
  [ProductTileModel.ProductLabelAttributes.Exclusive]:
    ProductCardModel.ProductLabelAttributes.OnlyOnNotino,
};

const BADGE_TAGS: Partial<
  Record<ProductTileModel.SpecialSales, ProductCardModel.ProductIcons>
> = {
  [ProductTileModel.SpecialSales.summerBlackFriday]:
    ProductCardModel.ProductIcons.SummerBlackFriday,
  [ProductTileModel.SpecialSales.blackFriday]:
    ProductCardModel.ProductIcons.BlackFriday,
  [ProductTileModel.SpecialSales.marginUnderLimit]:
    ProductCardModel.ProductIcons.MarginUnderLimit,
  [ProductTileModel.SpecialSales.recommendedByDermatologists]:
    ProductCardModel.ProductIcons.Dermo,
};

const isDefined = <T>(value: T | undefined): value is T => value !== undefined;

export const getProductTags = (
  attributes: AttributeType = {},
  { hideLabels = false, hideBadges = false } = {}
): IProductTag[] => {
  const badges = hideBadges
    ? []
    : transformProductIcons(attributes, noop)
        .map(({ icon }) => BADGE_TAGS[icon])
        .filter(isDefined);

  const labels = hideLabels
    ? []
    : transformAttributesToTags(attributes)
        .map((label) => LABEL_TAGS[label])
        .filter(isDefined);

  return [
    ...badges.map((value) => ({ type: ProductTagType.Badge, value })),
    ...labels.map((value) => ({ type: ProductTagType.Label, value })),
  ];
};

const toNameType = (type?: 'Brand' | 'Category') => {
  if (type === 'Brand') return DiscountNameType.Brand;
  if (type === 'Category') return DiscountNameType.Category;
  return DiscountNameType.Unknown;
};

const toConditionType = (type: 'Piece' | 'Price') =>
  type === 'Piece' ? 'Pieces' : 'Price';

interface IPromotionDataOptions {
  /** Converts a primary-currency amount for the dual price, when the shop shows one. */
  toSecondaryPrice?: (value: number) => number;
  /** Glamour Day's campaign text, which product-api does not translate. */
  glamourDayMessage?: string;
}

/**
 * Builds the card's promotionData from the voucher, Glamour Day and conditional
 * free delivery attributes, keeping their precedence: voucher, then Glamour Day,
 * then conditional free delivery.
 */
export const getPromotionData = (
  attributes: AttributeType = {},
  { toSecondaryPrice, glamourDayMessage }: IPromotionDataOptions = {}
): IPromotionData => {
  const toDual = (value?: number) =>
    value !== undefined && toSecondaryPrice
      ? toSecondaryPrice(value)
      : undefined;

  const promotionData: IPromotionData = {
    freeDelivery: Boolean(attributes[AttributeTypes.FreeDelivery]),
    isDiscount: false,
    voucherNotForCheapest: false,
  };

  const voucherDiscount = resolveVoucherDiscount({ attributes });

  if (voucherDiscount && isConditionalDiscount(voucherDiscount)) {
    const { discountConditions, targets, conditionType, discountType, code } =
      voucherDiscount;
    const best = discountConditions.reduce<
      (typeof discountConditions)[number] | undefined
    >(
      (prev, current) =>
        prev && prev.discountAmount > current.discountAmount ? prev : current,
      undefined
    );

    // reduce() without conditions has nothing to pick; render no discount
    if (!best) {
      return promotionData;
    }

    const isAbsolute = discountType === 'Absolute';
    const discount = isAbsolute ? best.discountAmount : best.percentageDiscount;

    return {
      ...promotionData,
      voucherCode: code,
      discountType: isAbsolute ? DiscountType.ABSOLUTE : DiscountType.RELATIVE,
      discountWithCondition: {
        name: targets[0]?.name,
        nameType: toNameType(targets[0]?.type),
        condition: best.conditionMin,
        conditionType: toConditionType(conditionType),
        discount,
        dualCondition:
          conditionType === 'Price' ? toDual(best.conditionMin) : undefined,
        dualDiscount: isAbsolute ? toDual(discount) : undefined,
      },
    };
  }

  if (voucherDiscount) {
    const { code, displayType, percentage, discountedPrice } = voucherDiscount;

    return {
      ...promotionData,
      isDiscount: true,
      voucherCode: code,
      discountType:
        displayType === 'Percentage'
          ? DiscountType.RELATIVE
          : DiscountType.ABSOLUTE,
      discountValue: percentage,
      discountedPrice,
      dualDiscountedPrice: toDual(discountedPrice),
    };
  }

  if (resolveGlamourDay(attributes)) {
    return { ...promotionData, localizedMessage: glamourDayMessage };
  }

  const conditionalFreeDelivery = attributes[
    AttributeTypes.ConditionalFreeDelivery
  ] as IConditionalFreeDelivery | undefined;

  if (conditionalFreeDelivery) {
    const { targets, type, value } = conditionalFreeDelivery;

    return {
      ...promotionData,
      freeDeliveryWithCondition: {
        name: targets[0]?.name,
        nameType: toNameType(targets[0]?.type),
        condition: value,
        conditionType: toConditionType(type),
        dualCondition: type === 'Price' ? toDual(value) : undefined,
      },
    };
  }

  return promotionData;
};

const LAST_PIECES = [
  StockAvailability.LAST1,
  StockAvailability.LAST2,
  StockAvailability.LAST3,
  StockAvailability.LAST4,
  StockAvailability.LAST5,
];

export const toCardStockAvailability = (
  stock: VpProduct['stockAvailability']
): StockAvailability => {
  switch (stock?.code) {
    case IStock.moreThan20:
      return StockAvailability.MORE_THAN20;
    case IStock.moreThan5:
      return StockAvailability.MORE_THAN5;
    case IStock.lessThan5: {
      const count = Math.min(Math.max(stock.count ?? 5, 1), 5);
      return LAST_PIECES[count - 1];
    }
    default:
      return StockAvailability.OUT_OF_STOCK;
  }
};

interface ICardProductOptions extends IPromotionDataOptions {
  url: string;
  annotation?: string | null;
  showPrice: boolean;
  showUnitPrice: boolean;
  hideLabels?: boolean;
  hideBadges?: boolean;
  isSponsored?: boolean;
}

export const toCardProduct = (
  product: VpProduct,
  {
    url,
    annotation,
    showPrice,
    showUnitPrice,
    hideLabels,
    hideBadges,
    isSponsored,
    toSecondaryPrice,
    glamourDayMessage,
  }: ICardProductOptions
): IProductProps => {
  const price = showPrice ? product.price?.value ?? 0 : 0;
  const unitPrice = showPrice && showUnitPrice ? product.unitPrice : null;

  return {
    id: Number(product.id),
    url,
    productCode: product.productCode ?? '',
    name: product.name ?? '',
    subName: product.subName ?? '',
    brandName: product.brand?.name ?? '',
    annotation: annotation ?? '',
    imageUrl: product.imageUrl ?? '',
    priceInformation: {
      price,
      dualPrice:
        price && toSecondaryPrice ? toSecondaryPrice(price) : undefined,
      unitPrice: unitPrice
        ? {
            pricePerUnit: unitPrice.value ?? 0,
            unit: {
              amount: unitPrice.perAmount ?? 0,
              unitName: unitPrice.unit ?? '',
            },
          }
        : undefined,
    },
    stockAvailability: toCardStockAvailability(product.stockAvailability),
    showGrouped: false,
    productTags: getProductTags(product.attributes, { hideLabels, hideBadges }),
    promotionData: getPromotionData(product.attributes, {
      toSecondaryPrice,
      glamourDayMessage,
    }),
    isSponsored,
    reviewInformation: product.reviewOverview?.rating
      ? {
          score: product.reviewOverview.rating,
          count: product.reviewOverview.count ?? 0,
        }
      : undefined,
  };
};
